import { memo, useMemo } from 'react';
import { Sprite } from '@pixi/react';
import { Texture } from 'pixi.js';
import { DungeonEntity } from '../../entities/DungeonEntity';
import { EffectTextDisplay } from './EffectTextDisplay';
import EntityDisplay from './EntityElements/EntityDisplay';
import { useSpritesStore } from '../../store/spritesSlice';
import { useGameStore } from '../../store/gameSlice';
import { tileHeight, tileWidth } from '../../data/tileset.config';
import { getZIndex } from '../../utils/zindex.utils';
import { resolveIconTexture } from '../../utils/iconTexture.utils';
import { dungeonService } from '../../services/DungeonService';

interface EntityRendererProps {
  entity: DungeonEntity;
  renderVersion: number;
}

const BUFF_ICON_SCALE = 0.5; // fraction of a tile

export default memo(({ entity }: EntityRendererProps) => {
  const clip = useMemo(
    () => entity.getRenderClip(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      entity.absoluteX,
      entity.absoluteY,
      entity.currentSpriteName,
      entity.direction,
    ],
  );

  const damageDisplays = entity.getDamageDisplays();
  const healDisplays = entity.getHealDisplays();
  const zoomFactor = useGameStore((s) => s.zoomFactor);
  const allTextures = useSpritesStore((s) => s.textures);

  // Show buff icons above every entity (monsters + remote players) EXCEPT the
  // local player, whose buffs are shown in the HUD (BuffList) instead.
  const buffsWithIcon =
    entity.id === dungeonService.localPlayerId
      ? []
      : entity
          .getActiveBuffs()
          .map((b) => ({ b, tex: resolveIconTexture(allTextures, b.def.icon) }))
          .filter((e): e is { b: (typeof e)['b']; tex: Texture } => !!e.tex);

  return (
    <>
      <EntityDisplay
        clip={clip}
        position={{ x: entity.absoluteX, y: entity.absoluteY }}
        name={entity.id}
        entityAlpha={entity.stats.opacity}
      />
      {damageDisplays.map((d) => (
        <EffectTextDisplay
          key={d.id}
          id={d.id}
          amount={d.damage}
          type={d.type}
          worldX={d.worldX}
          worldY={d.worldY}
          crit={d.crit}
          element={d.element}
          onComplete={(id) => entity.removeDamageDisplay(id)}
        />
      ))}
      {healDisplays.map((h) => (
        <EffectTextDisplay
          key={h.id}
          id={h.id}
          amount={h.heal}
          type={h.type}
          worldX={h.worldX}
          worldY={h.worldY}
          onComplete={(id) => entity.removeHealDisplay(id)}
        />
      ))}
      {buffsWithIcon.map(({ b, tex }, i) => {
        const iconSize = tileWidth * BUFF_ICON_SCALE * zoomFactor;
        // Bottom-centered on the entity's tile, spread horizontally when there
        // are several.
        const offsetX = (i - (buffsWithIcon.length - 1) / 2) * iconSize * 1.1;
        const screenX = entity.absoluteX * zoomFactor + offsetX;
        const screenY = (-entity.absoluteY + tileHeight - 10) * zoomFactor;
        return (
          <Sprite
            key={`${entity.id}-buff-${b.def.id}`}
            texture={tex}
            anchor={[0.5, 1]}
            x={screenX}
            y={screenY}
            width={iconSize}
            height={iconSize}
            zIndex={
              getZIndex({
                type: 'entity',
                posY: entity.absoluteY / tileHeight,
              }) + 1
            }
          />
        );
      })}
    </>
  );
});
